import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';
import { addProduct, updateProduct } from '../services/productService'; // ✅ Import updateProduct from service
import { getCategories, addCategory } from '../../companysettings/services/productCategoryService'; // ✅ Import addCategory
import { getVatRates } from '../../companysettings/services/vatService';
import { getSuppliers } from '../../suppliers/services/supplierService';
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { AddCircleOutline } from '@mui/icons-material';

const AddProductPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(id);
  
  // ✅ Try to get data from navigation state first
  const navigationData = location.state?.productData;
  
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  
  // ✅ Add state to track form changes for edit mode
  const [originalFormData, setOriginalFormData] = useState({});
  const [hasFormChanged, setHasFormChanged] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    barcode: '',
    imageUrl: '',
    categoryId: '',
    price: '',
    minSalePrice: '',
    costPrice: '',
    stockQuantity: '',
    minStockLevel: '',
    supplierId: '',
    vatId: '',
    isActive: true,
    isPromotionProduct: false,
    promotionDiscount: '',
    // ✅ Removed isVatIncludeInSalePrice
    isVATIncluded: true,
    syncToShopify: false,
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  // ✅ Categories state
  const [categories, setCategories] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [suppliers, setSuppliers] = useState([]); // ✅ Add suppliers state

  // Predefined options
  const supplierOptions = [
    { value: '', label: 'Select Supplier' },
    { value: 'acme-corp', label: 'ACME Corporation' },
    { value: 'tech-solutions', label: 'Tech Solutions Ltd' },
    { value: 'global-supplies', label: 'Global Supplies Inc' },
    { value: 'euro-distributors', label: 'Euro Distributors BV' },
    { value: 'quality-goods', label: 'Quality Goods SA' },
    { value: 'reliable-partners', label: 'Reliable Partners GmbH' },
    { value: 'premier-wholesale', label: 'Premier Wholesale' },
    { value: 'international-trade', label: 'International Trade Co' }
  ];

  // ✅ Function to compare form data with original data
  const checkIfFormChanged = (currentData, originalData) => {
    const currentStr = JSON.stringify(currentData);
    const originalStr = JSON.stringify(originalData);
    
    const hasChanged = currentStr !== originalStr;
    
    return hasChanged;
  };

  // ✅ Helper function to populate form data
  const populateFormData = (product) => {
    const populatedData = {
      name: product.Name || product.ProductName || product.name || '',
      description: product.Description || product.description || '',
      sku: product.SKU || product.Sku || product.sku || '',
      barcode: product.Barcode || product.BarCode || product.barcode || '', // ✅ Fixed: Added all variations
      imageUrl: product.ImageUrl || product.imageUrl || '',
      categoryId: product.ProductCategoryId ? String(product.ProductCategoryId) : '',
      price: product.SalePrice ? String(product.SalePrice) : '',
      minSalePrice: product.MinSalePrice ? String(product.MinSalePrice) : '',
      costPrice: product.CostPrice ? String(product.CostPrice) : '',
      stockQuantity: product.StockQuantity || product.StockQuanity ? String(product.StockQuantity || product.StockQuanity) : '',
      minStockLevel: product.MinStockLevel ? String(product.MinStockLevel) : '',
      supplierId: product.SupplierId ? String(product.SupplierId) : '',
      vatId: product.VatId ? String(product.VatId) : '',
      isActive: product.IsActive !== undefined ? product.IsActive : true,
      isPromotionProduct: product.IsPromotionProduct || product.isPromotionProduct || false,
      promotionDiscount: product.PromotionPercentage ? String(product.PromotionPercentage) : '',
      // ✅ Removed isVatIncludeInSalePrice
      isVATIncluded: product.IsVATIncluded !== undefined ? product.IsVATIncluded : true,
      syncToShopify: product.SyncToShopify || false
    };
    
    // ✅ Set both current form data and original data for edit mode
    setFormData(populatedData);
    if (isEditMode) {
      setOriginalFormData({ ...populatedData });
      setHasFormChanged(false);
    }
  };

  // ✅ Fallback function to load data from API
  const loadProductDataFromAPI = async () => {
    if (!id) {
      toast.error('Product ID is missing');
      navigate('/products');
      return;
    }

    setDataLoading(true);
    try {
      
      const response = await axiosInstance.get(`Products/${id}`);
      
      if (response.data.status === 'Success' && response.data.payload) {
        populateFormData(response.data.payload);
      } else {
        throw new Error(response.data.message || 'Product not found');
      }
    } catch (error) {
      
      if (error.response?.status === 404) {
        toast.error('Product not found');
      } else {
        toast.error('Failed to load product data');
      }
      navigate('/products');
    } finally {
      setDataLoading(false);
    }
  };

  // ✅ Hybrid data loading logic
  useEffect(() => {
    if (!isEditMode) return;

    if (navigationData) {
      // ✅ Best case: Use data from navigation state (no API call needed)
      populateFormData(navigationData);
    } else {
      // ✅ Fallback: Fetch from API (for direct URL access)
      loadProductDataFromAPI();
    }
  }, [isEditMode, navigationData]);

  // ✅ Fetch categories and VAT rates on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // ✅ Fetch categories with error handling
        try {
          const categoriesResponse = await getCategories();
          const categoryData = categoriesResponse?.payload || categoriesResponse || [];
          
          // ✅ Ensure it's always an array
          if (Array.isArray(categoryData)) {
            setCategories(categoryData);
          } else {
            setCategories([]);
          }
        } catch (catError) {
          console.error('Error fetching categories:', catError);
          setCategories([]);
        }

        // ✅ Fetch VAT rates with error handling
        try {
          const vatResponse = await getVatRates();
          const vatData = vatResponse?.payload || vatResponse || [];
          
          // ✅ Ensure it's always an array
          if (Array.isArray(vatData)) {
            setVatRates(vatData);
          } else {
            setVatRates([]);
          }
        } catch (vatError) {
          console.error('Error fetching VAT rates:', vatError);
          setVatRates([]);
        }

        // ✅ Fetch suppliers with error handling
        try {
          const suppliersResponse = await getSuppliers();
          const supplierData = suppliersResponse?.payload || suppliersResponse || [];
          
          // ✅ Ensure it's always an array
          if (Array.isArray(supplierData)) {
            setSuppliers(supplierData);
          } else {
            setSuppliers([]);
          }
        } catch (suppError) {
          console.error('Error fetching suppliers:', suppError);
          setSuppliers([]);
        }
        
      } catch (error) {
        console.error('Critical error during data fetch:', error);
        
        // ✅ Set empty arrays to prevent crashes
        setCategories([]);
        setVatRates([]);
        setSuppliers([]);
      }
    };

    fetchData();
  }, []);

  // ✅ Add state to track user interaction and form submission attempts
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [touchedFields, setTouchedFields] = useState({}); // Track which fields user has touched

  // ✅ Add state to track if form is currently valid (for button state only)
  const [isFormValid, setIsFormValid] = useState(false);

  // ✅ Validation function that only calculates validity, doesn't set errors
  const calculateFormValidity = () => {
    // Required fields only
    if (!formData.name.trim()) return false;
    if (formData.name.length > 100) return false;
    
    if (!formData.categoryId) return false;
    if (!formData.supplierId) return false;
    if (!formData.vatId) return false;
    
    if (!formData.price || parseFloat(formData.price) < 0 || parseFloat(formData.price) > 100000) return false;
    if (!formData.costPrice || parseFloat(formData.costPrice) < 0 || parseFloat(formData.costPrice) > 100000) return false;
    if (!formData.stockQuantity || parseFloat(formData.stockQuantity) < 0 || parseFloat(formData.stockQuantity) > 100000) return false;
    
    // Optional fields validation (only if filled)
    if (formData.sku && (formData.sku.length < 3 || formData.sku.length > 30)) return false;
    if (formData.barcode && (formData.barcode.length < 3 || formData.barcode.length > 30)) return false;
    if (formData.description && formData.description.length > 300) return false;
    
    if (formData.imageUrl) {
      if (formData.imageUrl.length > 300) return false;
      try {
        new URL(formData.imageUrl);
      } catch {
        return false;
      }
    }
    
    if (formData.minSalePrice) {
      const minPrice = parseFloat(formData.minSalePrice);
      if (minPrice < 0 || minPrice > 100000) return false;
    }
    
    if (formData.minStockLevel) {
      const minStock = parseFloat(formData.minStockLevel);
      if (minStock < 0 || minStock > 100000) return false;
    }
    
    if (formData.isPromotionProduct) {
      const discount = parseFloat(formData.promotionDiscount);
      if (!formData.promotionDiscount || discount < 0 || discount > 100) return false;
    }
    
    return true;
  };

  // ✅ Update form validity when form data changes (for button state)
  useEffect(() => {
    const isValid = calculateFormValidity();
    setIsFormValid(isValid);
  }, [
    formData.name,
    formData.sku,
    formData.categoryId,
    formData.vatId,
    formData.price,
    formData.costPrice,
    formData.stockQuantity,
    formData.minStockLevel,
    formData.description,
    formData.isPromotionProduct,
    formData.promotionDiscount
  ]);

  // ✅ Function to validate and show errors (only when needed)
  const validateAndShowErrors = () => {
    const newErrors = {};

    // Required: Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name cannot exceed 100 characters';
    }

    // Required: Category validation
    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }

    // Required: Supplier validation
    if (!formData.supplierId) {
      newErrors.supplierId = 'Supplier is required';
    }

    // Required: VAT validation
    if (!formData.vatId) {
      newErrors.vatId = 'VAT rate is required';
    }

    // Required: Sale Price validation
    if (!formData.price) {
      newErrors.price = 'Sale price is required';
    } else {
      const price = parseFloat(formData.price);
      if (price < 0 || price > 100000) {
        newErrors.price = 'Sale price must be between 0 and 100,000';
      }
    }

    // Required: Cost Price validation
    if (!formData.costPrice) {
      newErrors.costPrice = 'Cost price is required';
    } else {
      const costPrice = parseFloat(formData.costPrice);
      if (costPrice < 0 || costPrice > 100000) {
        newErrors.costPrice = 'Cost price must be between 0 and 100,000';
      }
    }

    // Required: Stock Quantity validation
    if (!formData.stockQuantity) {
      newErrors.stockQuantity = 'Stock quantity is required';
    } else {
      const stock = parseFloat(formData.stockQuantity);
      if (stock < 0 || stock > 100000) {
        newErrors.stockQuantity = 'Stock quantity must be between 0 and 100,000';
      }
    }

    // Optional fields - only validate if filled
    if (formData.sku && (formData.sku.length < 3 || formData.sku.length > 30)) {
      newErrors.sku = 'SKU must be between 3-30 characters';
    }

    if (formData.barcode && (formData.barcode.length < 3 || formData.barcode.length > 30)) {
      newErrors.barcode = 'Barcode must be between 3-30 characters';
    }

    if (formData.description && formData.description.length > 300) {
      newErrors.description = 'Description cannot exceed 300 characters';
    }

    // ✅ Enhanced Image URL validation
    if (formData.imageUrl) {
      if (formData.imageUrl.length > 300) {
        newErrors.imageUrl = 'Image URL cannot exceed 300 characters';
      } else {
        try {
          const url = new URL(formData.imageUrl);
          // ✅ Check if it's a valid http/https URL
          if (!['http:', 'https:'].includes(url.protocol)) {
            newErrors.imageUrl = 'URL must start with http:// or https://';
          }
        } catch {
          newErrors.imageUrl = 'Please enter a valid URL (e.g., https://example.com/image.jpg)';
        }
      }
    }

    if (formData.minSalePrice) {
      const minPrice = parseFloat(formData.minSalePrice);
      if (minPrice < 0 || minPrice > 100000) {
        newErrors.minSalePrice = 'Min sale price must be between 0 and 100,000';
      }
    }

    if (formData.minStockLevel) {
      const minStock = parseFloat(formData.minStockLevel);
      if (minStock < 0 || minStock > 100000) {
        newErrors.minStockLevel = 'Min stock level must be between 0 and 100,000';
      }
    }

    if (formData.isPromotionProduct) {
      const discount = parseFloat(formData.promotionDiscount);
      if (!formData.promotionDiscount || discount < 0 || discount > 100) {
        newErrors.promotionDiscount = 'Promotion percentage must be between 0-100%';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Update validateSingleField for imageUrl
  const validateSingleField = (fieldName, value) => {
    if (!hasAttemptedSubmit) return;

    let error = '';
    
    switch (fieldName) {
      // Required fields
      case 'name':
        if (!value.trim()) {
          error = 'Product name is required';
        } else if (value.length > 100) {
          error = 'Name cannot exceed 100 characters';
        }
        break;
      case 'categoryId':
        if (!value) {
          error = 'Category is required';
        }
        break;
      case 'supplierId':
        if (!value) {
          error = 'Supplier is required';
        }
        break;
      case 'vatId':
        if (!value) {
          error = 'VAT rate is required';
        }
        break;
      case 'price':
        if (!value) {
          error = 'Sale price is required';
        } else {
          const price = parseFloat(value);
          if (price < 0 || price > 100000) {
            error = 'Sale price must be between 0 and 100,000';
          }
        }
        break;
      case 'costPrice':
        if (!value) {
          error = 'Cost price is required';
        } else {
          const costPrice = parseFloat(value);
          if (costPrice < 0 || costPrice > 100000) {
            error = 'Cost price must be between 0 and 100,000';
          }
        }
        break;
      case 'stockQuantity':
        if (!value) {
          error = 'Stock quantity is required';
        } else {
          const stock = parseFloat(value);
          if (stock < 0 || stock > 100000) {
            error = 'Stock quantity must be between 0 and 100,000';
          }
        }
        break;
      
      // Optional fields - only validate if filled
      case 'sku':
        if (value && (value.length < 3 || value.length > 30)) {
          error = 'SKU must be between 3-30 characters';
        }
        break;
      case 'barcode':
        if (value && (value.length < 3 || value.length > 30)) {
          error = 'Barcode must be between 3-30 characters';
        }
        break;
      case 'description':
        if (value && value.length > 300) {
          error = 'Description cannot exceed 300 characters';
        }
        break;
      case 'imageUrl':
        if (value) {
          if (value.length > 300) {
            error = 'Image URL cannot exceed 300 characters';
          } else {
            try {
              const url = new URL(value);
              if (!['http:', 'https:'].includes(url.protocol)) {
                error = 'URL must start with http:// or https://';
              }
            } catch {
              error = 'Please enter a valid URL (e.g., https://example.com/image.jpg)';
            }
          }
        }
        break;
      case 'minSalePrice':
        if (value) {
          const minPrice = parseFloat(value);
          if (minPrice < 0 || minPrice > 100000) {
            error = 'Min sale price must be between 0 and 100,000';
          }
        }
        break;
      case 'minStockLevel':
        if (value) {
          const minStock = parseFloat(value);
          if (minStock < 0 || minStock > 100000) {
            error = 'Min stock level must be between 0 and 100,000';
          }
        }
        break;
      case 'promotionDiscount':
        if (formData.isPromotionProduct) {
          const discount = parseFloat(value);
          if (!value || discount < 0 || discount > 100) {
            error = 'Promotion percentage must be between 0-100%';
          }
        }
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  // ✅ Update handleInputChange to track user interaction
  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };
      
      if (isEditMode) {
        const formChanged = checkIfFormChanged(updated, originalFormData);
        setHasFormChanged(formChanged);
      }
      
      return updated;
    });
    
    if (errors[name] && touchedFields[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // ✅ Add onBlur handler for field validation
  const handleFieldBlur = (event) => {
    const { name, value } = event.target;
    
    // ✅ Only validate if user has interacted or attempted submit
    if (hasUserInteracted || hasAttemptedSubmit) {
      validateSingleField(name, value);
    }
  };

  // ✅ Updated handleSubmit - prevent HTML5 validation, use custom validation
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation(); // ✅ Prevent HTML5 validation
    
    setHasAttemptedSubmit(true);
    
    // ✅ Always validate on submit
    if (!validateAndShowErrors()) {
      toast.error('Please fill in all required fields');
      
      // ✅ Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }

    if (isEditMode && !hasFormChanged) {
      toast.warning('No changes detected. Please modify the form to update.');
      return;
    }

    setLoading(true);

    try {
      const productData = {
        Name: formData.name.trim(),
        SKU: formData.sku.trim() || null,
        BarCode: formData.barcode.trim() || null,
        Description: formData.description.trim() || null,
        ImageUrl: formData.imageUrl.trim() || null,
        SalePrice: parseFloat(formData.price),
        // ✅ Removed IsVatIncludeInSalePrice
        MinSalePrice: formData.minSalePrice ? parseFloat(formData.minSalePrice) : 0,
        CostPrice: parseFloat(formData.costPrice),
        StockQuantity: parseFloat(formData.stockQuantity),
        MinStockLevel: formData.minStockLevel ? parseFloat(formData.minStockLevel) : 0,
        IsVATIncluded: formData.isVATIncluded,
        VATPercentage: formData.vatId && Array.isArray(vatRates) ? 
          (vatRates.find(vat => String(vat?.Id) === String(formData.vatId))?.VATPercentage || 0) 
          : 0,
        IsPromotionProduct: formData.isPromotionProduct,
        PromotionPercentage: formData.isPromotionProduct 
          ? parseFloat(formData.promotionDiscount) 
          : 0,
        VatId: parseInt(formData.vatId),
        ProductCategoryId: parseInt(formData.categoryId),
        SupplierId: parseInt(formData.supplierId),
        IsActive: formData.isActive,
        IsWishlistProduct: false,
        SyncToShopify: formData.syncToShopify
      };

      let response;
      if (isEditMode) {
        const updatePayload = {
          ...productData,
          Id: parseInt(id)
        };
        
        response = await updateProduct(updatePayload);
        toast.success('Product updated successfully!');
      } else {
        response = await addProduct(productData);
        toast.success('Product added successfully!');
      }
      
      navigate('/products', {
        state: { 
          message: isEditMode ? 'Product updated successfully' : 'Product added successfully',
          productId: response.Id || response.id || id
        }
      });

    } catch (error) {
      console.error('Error saving product:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        toast.error('Please fix the form errors');
      } else {
        toast.error(isEditMode ? 'Failed to update product' : 'Failed to add product');
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Reset form function
  const handleResetForm = () => {
    setFormData({ ...originalFormData });
    setHasFormChanged(false);
    setErrors({});
    setTouchedFields({});
    setHasUserInteracted(false);
    setHasAttemptedSubmit(false);
    toast.info('Form reset to original values');
  };

  // ✅ Calculate if button should be disabled
  const isButtonDisabled = useMemo(() => {
    return loading || (isEditMode && !hasFormChanged);
  }, [loading, isEditMode, hasFormChanged]);

  // ✅ Helper function to determine if error should be shown
  const shouldShowError = (fieldName) => {
    return hasAttemptedSubmit && !!errors[fieldName];
  };

  // ✅ Show loading spinner while data is being fetched (only for API fallback)
  if (dataLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 400,
          gap: 2
        }}>
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Loading product data...
          </Typography>
        </Box>
      </Container>
    );
  }

  // ✅ Add the missing handleCancel function
  const handleCancel = () => {
    // Check if user has made any changes to the form
    const hasAnyData = Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'boolean') return value !== true; // assuming default is true
      return value !== '';
    });

    // If in edit mode and form has changes, or if in add mode and has data
    if ((isEditMode && hasFormChanged) || (!isEditMode && hasAnyData)) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/products');
      }
    } else {
      // No changes, safe to navigate
      navigate('/products');
    }
  };

  // ✅ Add state for dialogs
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [openVATDialog, setOpenVATDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '' });
  const [newSupplier, setNewSupplier] = useState({ name: '', email: '', phone: '', address: '' });
  const [newVAT, setNewVAT] = useState({ percentage: '', description: '' });

  // ✅ Add handlers for category dialog
  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      // ✅ Use the service method to add category
      const response = await addCategory({
        CategoryName: newCategory.name
      });

      if (response.status === 'Success' || response) {
        const addedCategory = response.payload || response;
        toast.success('Category added successfully');
        
        // Refresh categories using service
        const categoriesResponse = await getCategories();
        const categoriesData = categoriesResponse.payload || [];
        setCategories(categoriesData);
        
        // Set the newly added category
        setFormData(prev => ({ ...prev, categoryId: String(addedCategory.Id) }));
        setOpenCategoryDialog(false);
        setNewCategory({ name: '' });
      }
    } catch (error) {
      toast.error('Failed to add category');
      console.error(error);
    }
  };

  // ✅ Add handlers for supplier dialog
  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) {
      toast.error('Supplier name is required');
      return;
    }

    try {
      const response = await axiosInstance.post('Suppliers', {
        Name: newSupplier.name,
        Email: newSupplier.email,
        Phone: newSupplier.phone,
        Address: newSupplier.address
      });

      if (response.data.status === 'Success') {
        const addedSupplier = response.data.payload;
        toast.success('Supplier added successfully');
        
        // Refresh suppliers
        const suppliersResponse = await getSuppliers();
        const suppliersData = suppliersResponse.payload || suppliersResponse || [];
        setSuppliers(suppliersData);
        
        // Set the newly added supplier
        setFormData(prev => ({ ...prev, supplierId: String(addedSupplier.Id) }));
        setOpenSupplierDialog(false);
        setNewSupplier({ name: '', email: '', phone: '', address: '' });
      }
    } catch (error) {
      toast.error('Failed to add supplier');
      console.error(error);
    }
  };

  // ✅ Handle Add VAT
  const handleAddVAT = async () => {
    if (!newVAT.percentage || parseFloat(newVAT.percentage) <= 0) {
      toast.error('Please enter a valid VAT percentage');
      return;
    }
    
    try {
      const response = await axiosInstance.post('VAT', {
        VATPercentage: parseFloat(newVAT.percentage),
        Description: newVAT.description || null
      });

      if (response.data.status === 'Success') {
        const addedVAT = response.data.payload;
        toast.success('VAT rate added successfully');
        
        // Refresh VAT rates
        const vatResponse = await getVatRates();
        const vatData = vatResponse.payload || vatResponse || [];
        setVatRates(vatData);
        
        // Set the newly added VAT
        setFormData(prev => ({ ...prev, vatId: String(addedVAT.Id) }));
        setOpenVATDialog(false);
        setNewVAT({ percentage: '', description: '' });
      }
    } catch (error) {
      toast.error('Failed to add VAT rate');
      console.error(error);
    }
  };

  // ✅ Handle category change (detect "add new" option)
  const handleCategoryChange = (event) => {
    const value = event.target.value;
    if (value === 'ADD_NEW') {
      setOpenCategoryDialog(true);
    } else {
      handleInputChange(event);
    }
  };

  // ✅ Handle supplier change (detect "add new" option)
  const handleSupplierChange = (event) => {
    const value = event.target.value;
    if (value === 'ADD_NEW') {
      setOpenSupplierDialog(true);
    } else {
      handleInputChange(event);
    }
  };

  // ✅ Handle VAT change (detect "add new" option)
  const handleVATChange = (event) => {
    const value = event.target.value;
    if (value === 'ADD_NEW') {
      setOpenVATDialog(true);
    } else {
      handleInputChange(event);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
      {/* ✅ Dynamic Header */}
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h5" 
          component="h1" 
          sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            textAlign: 'left', 
            mb: 0.5 
          }}
        >
          {isEditMode ? 'Edit Product' : 'Add Product'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEditMode ? 'Update the product information below' : 'Fill in the product information below'}
        </Typography>
        {/* ✅ Show data source indicator in edit mode */}
        {isEditMode && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
            {navigationData ? '📦 Data loaded from cache' : '🌐 Data loaded from server'}
          </Typography>
        )}
      </Box>

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2.5}>
            
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  Basic Information
                </Typography>
              </Box>
            </Grid>

            {/* Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('name')}
                helperText={shouldShowError('name') ? errors.name : 'Max 100 characters'}
                required
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 100 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('name') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('name') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* SKU */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="SKU"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('sku')}
                helperText={shouldShowError('sku') ? errors.sku : '3-30 characters (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ 
                  style: { textTransform: 'uppercase' },
                  maxLength: 30
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('sku') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('sku') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Barcode */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Barcode"
                name="barcode"
                value={formData.barcode}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('barcode')}
                helperText={shouldShowError('barcode') ? errors.barcode : '3-30 characters (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 30 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('barcode') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('barcode') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('description')}
                helperText={shouldShowError('description') ? errors.description : 'Max 300 characters (optional)'}
                disabled={loading}
                multiline
                rows={3}
                size="medium"
                inputProps={{ maxLength: 300 }}
                placeholder="Detailed product description..."
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('description') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('description') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={4}>
              <FormControl 
                fullWidth 
                error={shouldShowError('categoryId')}
                disabled={loading}
                size="medium"
                required
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('categoryId') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('categoryId') ? 2 : 1,
                    }
                  }
                }}
              >
                <InputLabel>Category *</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId || ''}
                  onChange={handleCategoryChange}
                  onBlur={handleFieldBlur}
                  label="Category *"
                  required
                >
                  <MenuItem value="ADD_NEW" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                    Add New Category
                  </MenuItem>
                  
                  {categories.length === 0 ? (
                    <MenuItem disabled value="">
                      <Typography variant="body2" color="text.secondary">
                        No categories available
                      </Typography>
                    </MenuItem>
                  ) : (
                    categories.map((category, index) => (
                      <MenuItem 
                        key={category.Id || index}
                        value={String(category.Id)}
                      >
                        {category.CategoryName}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {shouldShowError('categoryId') && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5, fontWeight: 500 }}>
                    {errors.categoryId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Supplier */}
            <Grid item xs={12} md={4}>
              <FormControl 
                fullWidth 
                error={shouldShowError('supplierId')}
                disabled={loading}
                size="medium"
                required
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('supplierId') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('supplierId') ? 2 : 1,
                    }
                  }
                }}
              >
                <InputLabel>Supplier *</InputLabel>
                <Select
                  name="supplierId"
                  value={formData.supplierId || ''}
                  onChange={handleSupplierChange}
                  onBlur={handleFieldBlur}
                  label="Supplier *"
                  required
                >
                  <MenuItem value="ADD_NEW" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                    Add New Supplier
                  </MenuItem>
                  
                  {suppliers.length === 0 ? (
                    <MenuItem disabled value="">
                      <Typography variant="body2" color="text.secondary">
                        No suppliers available
                      </Typography>
                    </MenuItem>
                  ) : (
                    suppliers.map((supplier, index) => (
                      <MenuItem 
                        key={supplier.Id || index}
                        value={String(supplier.Id)}
                      >
                        {supplier.Name || supplier.SupplierName || 'Unknown Supplier'}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {shouldShowError('supplierId') && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5, fontWeight: 500 }}>
                    {errors.supplierId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* VAT Rate */}
            <Grid item xs={12} md={4}>
              <FormControl 
                fullWidth 
                error={shouldShowError('vatId')}
                disabled={loading}
                size="medium"
                required
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('vatId') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('vatId') ? 2 : 1,
                    }
                  }
                }}
              >
                <InputLabel>VAT Rate *</InputLabel>
                <Select
                  name="vatId"
                  value={formData.vatId || ''}
                  onChange={handleVATChange}
                  onBlur={handleFieldBlur}
                  label="VAT Rate *"
                  required
                >
                  <MenuItem value="ADD_NEW" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                    Add New VAT Rate
                  </MenuItem>
                  
                  {!Array.isArray(vatRates) || vatRates.length === 0 ? (
                    <MenuItem disabled value="">
                      <Typography variant="body2" color="text.secondary">
                        No VAT rates available
                      </Typography>
                    </MenuItem>
                  ) : (
                    vatRates.map((vatRate, index) => (
                      <MenuItem 
                        key={vatRate?.Id || index}
                        value={String(vatRate?.Id || '')}
                      >
                        {`${vatRate?.VATPercentage || 0}%`}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {shouldShowError('vatId') && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5, fontWeight: 500 }}>
                    {errors.vatId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Pricing & Stock Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  Pricing & Stock
                </Typography>
              </Box>
            </Grid>

            {/* Cost Price */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Cost Price"
                name="costPrice"
                type="number"
                value={formData.costPrice}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('costPrice')}
                helperText={shouldShowError('costPrice') ? errors.costPrice : '0 - 100,000'}
                required
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('costPrice') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('costPrice') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Sale Price */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Sale Price"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('price')}
                helperText={shouldShowError('price') ? errors.price : '0 - 100,000'}
                required
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('price') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('price') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Min Sale Price */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Min Sale Price"
                name="minSalePrice"
                type="number"
                value={formData.minSalePrice}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('minSalePrice')}
                helperText={shouldShowError('minSalePrice') ? errors.minSalePrice : '0 - 100,000 (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('minSalePrice') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('minSalePrice') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Stock Quantity */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Stock Quantity"
                name="stockQuantity"
                type="number"
                value={formData.stockQuantity}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('stockQuantity')}
                helperText={shouldShowError('stockQuantity') ? errors.stockQuantity : '0 - 100,000'}
                required
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 1 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('stockQuantity') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('stockQuantity') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Min Stock Level */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Min Stock Level"
                name="minStockLevel"
                type="number"
                value={formData.minStockLevel}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('minStockLevel')}
                helperText={shouldShowError('minStockLevel') ? errors.minStockLevel : 'Reorder threshold (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ min: 0, max: 100000, step: 1 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('minStockLevel') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('minStockLevel') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Additional Settings Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  Additional Settings
                </Typography>
              </Box>
            </Grid>

            {/* ✅ Image URL - MOVED HERE */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Image URL"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('imageUrl')}
                helperText={shouldShowError('imageUrl') ? errors.imageUrl : 'Valid URL starting with http:// or https:// (optional)'}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 300 }}
                placeholder="https://example.com/image.jpg"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '& fieldset': {
                      borderColor: shouldShowError('imageUrl') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('imageUrl') ? 2 : 1,
                    }
                  }
                }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={3}>
              <FormControl 
                fullWidth 
                disabled={loading}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Promotion Product Toggle */}
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    name="isPromotionProduct"
                    checked={formData.isPromotionProduct}
                    onChange={handleInputChange}
                    disabled={loading}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Promotion Product
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mark as promotional item
                    </Typography>
                  </Box>
                }
                sx={{ 
                  '& .MuiFormControlLabel-label': { ml: 1 },
                  alignItems: 'flex-start',
                  mt: 1 
                }}
              />
            </Grid>

            {/* Promotion Discount */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Promotion Discount"
                name="promotionDiscount"
                type="number"
                value={formData.promotionDiscount}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={shouldShowError('promotionDiscount')}
                helperText={shouldShowError('promotionDiscount') ? errors.promotionDiscount : 'Discount % (0-100)'}
                disabled={loading || !formData.isPromotionProduct}
                size="medium"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    backgroundColor: !formData.isPromotionProduct ? 'action.disabled' : 'inherit',
                    '& fieldset': {
                      borderColor: shouldShowError('promotionDiscount') ? 'error.main' : undefined,
                      borderWidth: shouldShowError('promotionDiscount') ? 2 : 1,
                    }
                  }
                }}
                placeholder={formData.isPromotionProduct ? "Enter %" : "Enable promotion"}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end', 
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                {isEditMode && hasFormChanged && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleResetForm}
                    disabled={loading}
                    sx={{ 
                      minWidth: 120,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Reset
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Cancel
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isButtonDisabled}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 2,
                    opacity: isButtonDisabled ? 0.6 : 1
                  }}
                >
                  {loading 
                    ? (isEditMode ? 'Updating...' : 'Creating...') 
                    : (isEditMode ? 'Update Product' : 'Create Product')
                  }
                </Button>
              </Box>
              
              {isEditMode && !hasFormChanged && !loading && (
                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">
                    Make changes to enable update
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* ✅ Add Category Dialog */}
      <Dialog 
        open={openCategoryDialog} 
        onClose={() => setOpenCategoryDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            required
            value={newCategory.name}
            onChange={(e) => setNewCategory({ name: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setOpenCategoryDialog(false);
              setNewCategory({ name: '' });
            }}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddCategory} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
          >
            Add Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Add Supplier Dialog */}
      <Dialog 
        open={openSupplierDialog} 
        onClose={() => setOpenSupplierDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Supplier</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Supplier Name"
            fullWidth
            required
            value={newSupplier.name}
            onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={newSupplier.email}
            onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
          <TextField
            margin="dense"
            label="Phone"
            fullWidth
            value={newSupplier.phone}
            onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
          <TextField
            margin="dense"
            label="Address"
            fullWidth
            multiline
            rows={2}
            value={newSupplier.address}
            onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setOpenSupplierDialog(false);
              setNewSupplier({ name: '', email: '', phone: '', address: '' });
            }}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddSupplier} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
          >
            Add Supplier
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Add VAT Dialog */}
      <Dialog 
        open={openVATDialog} 
        onClose={() => setOpenVATDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add New VAT Rate</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="VAT Percentage"
            type="number"
            fullWidth
            required
            value={newVAT.percentage}
            onChange={(e) => setNewVAT({ ...newVAT, percentage: e.target.value })}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            value={newVAT.description}
            onChange={(e) => setNewVAT({ ...newVAT, description: e.target.value })}
            sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            placeholder="e.g., Standard Rate, Reduced Rate"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setOpenVATDialog(false);
              setNewVAT({ percentage: '', description: '' });
            }}
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddVAT} 
            variant="contained" 
            color="primary"
            sx={{ borderRadius: 1.5 }}
          >
            Add VAT Rate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddProductPage;